import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useRoute, useSearch } from "wouter";
import Header from "@/components/Header";
import { ShoppingCart as CartDrawer } from "@/components/ShoppingCart";
import { HeatBadge } from "@/components/HeatBadge";
import { trackEvent } from "@/lib/analytics";
import { useCart } from "@/hooks/useCart";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { formatDistanceLabel, type ResolvedLocation } from "@/lib/geo";
import type { CartItemWithDetails, Item, User, MadeToOrderField, CustomResponse } from "@shared/schema";
import { ListingPhotoPlaceholder } from "@/components/ListingPhotoPlaceholder";
import { MadeToOrderFields, madeToOrderResponsesComplete } from "@/components/MadeToOrderFields";
import { stripDemoMarker } from "@/lib/demo";
import { formatWeight, priceForUnit, unitDisplayLabel, collapseUnitChoices, unitsAreNamed } from "@shared/weight";
import { WeightSizePicker, type ResolvedWeightUnit } from "@/components/WeightSizePicker";
import { useSwipePager } from "@/hooks/useSwipePager";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useSavedItems } from "@/hooks/useSavedItems";
import { TapBurst, useTapBurst, useSurfaceDoubleTap } from "@/components/TapBurst";
import {
  isClothingItem,
  getListingImages,
  getClothingAttributes,
  garmentLabel,
  conditionLabel,
  saleInfo,
} from "@shared/clothing";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Heart,
  Loader2,
  MapPin,
  Maximize2,
  Minus,
  Plus,
  Share2,
  ShoppingBasket,
  Sprout,
  Star,
  Store,
  Truck,
  X,
  Flag,
} from "lucide-react";
import { ReportDialog } from "@/components/ReportDialog";
import { BarterPanel } from "@/components/BarterPanel";

// Share links carry a readable name slug before the id
// (/items/marinara-sauce-<uuid>). This pulls the real UUID off the end; a
// plain "<uuid>" param matches itself, so pre-slug links keep resolving.
const ITEM_UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function itemSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

type BuyerFulfillmentOption = {
  id: string;
  fulfillmentMethod: "seller_pickup" | "drop_zone_pickup" | "local_delivery";
  buyerLabel: string;
  description?: string | null;
  dayLabel?: string | null;
  timeSlots?: {
    id: string;
    dayOfWeek: string;
    startTime: string;
    endTime: string;
  }[];
  dropZone?: {
    id: string;
    name: string;
    city: string;
    state: string;
    zipCode: string;
    pickupWindowLabel?: string | null;
  } | null;
};

type WeightUnitOption = { id: string; weight: number; priceCents: number; label?: string | null };

type ItemDetail = Item & {
  seller?: (User & { averageRating?: number; reviewCount?: number }) | null;
  subtype?: string | null;
  shortDescription?: string | null;
  attributesJson?: {
    bundleSize?: string | null;
    harvestTiming?: string | null;
    [key: string]: unknown;
  } | null;
  soldByWeight?: boolean;
  sizeTier?: string | null;
  weightMin?: number | null;
  weightMax?: number | null;
  unitsAvailable?: number | null;
  pricePerLbCents?: number | null;
  availableUnits?: WeightUnitOption[];
};

function prettyLabel(value?: string | null) {
  if (!value) return null;
  if (value === "farmstand") return "Farm Stand";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

type GrowthPhoto = {
  id: string;
  photoUrl: string;
  note: string | null;
  takenAt: string;
};

function GrowthPhotosStrip({ itemId }: { itemId: string }) {
  const { data: photos = [] } = useQuery<GrowthPhoto[]>({
    queryKey: ["/api/items", itemId, "growth-photos"],
    queryFn: async () => {
      const res = await fetch(`/api/items/${itemId}/growth-photos`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: Boolean(itemId),
  });
  if (photos.length === 0) {
    return (
      <p className="text-[11px] text-emerald-900/60 italic">
        No progress photos yet. The seller will post one each week so you can watch it ripen.
      </p>
    );
  }
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-800/70">
        Recent progress
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {photos.slice(0, 6).map((photo) => (
          <div key={photo.id} className="shrink-0 space-y-1">
            <img
              src={photo.photoUrl}
              alt="Growth update"
              className="h-20 w-20 rounded-lg object-cover"
              loading="lazy"
            />
            <div className="text-[9px] text-emerald-800/70">
              {new Date(photo.takenAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const [match, params] = useRoute("/items/:id");
  const search = useSearch();
  if (!match) return null;
  const itemId = ((params.id ?? "").match(ITEM_UUID_RE)?.[0] ?? params.id) as string;
  const backHref = new URLSearchParams(search).get("back") || "";
  return <ListingScreen itemId={itemId} backHref={backHref} />;
}

// preview renders the same page inert -- no header, no overlays, no analytics,
// no swipe of its own -- so the panel sliding in under the finger paints
// exactly what the landed page will.
function ListingScreen({
  itemId,
  backHref,
  preview = false,
}: {
  itemId: string;
  backHref: string;
  preview?: boolean;
}) {
  const [, setLocation] = useLocation();

  const buyerLocation = useMemo<ResolvedLocation | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem("marketlink_buyer_location");
      return raw ? (JSON.parse(raw) as ResolvedLocation) : null;
    } catch {
      return null;
    }
  }, []);

  const queryClient = useQueryClient();
  const { cart, addToCart, updateQuantity, removeFromCart, clearCart, cartCount } = useCart();
  const { user: authedUser } = useAuth();
  const { savedSet, toggleSave } = useSavedItems();
  const { burst, fire: fireBurst, clear: clearBurst } = useTapBurst();
  const isSaved = savedSet.has(itemId);
  // Double tap only ever likes -- never unlikes -- and always shows the burst,
  // so a repeat double tap confirms rather than silently undoing (Instagram
  // semantics). Guests get the sign-in toast instead of a burst that lies.
  const likeFromGesture = () => {
    if (preview) return;
    if (!authedUser) {
      toast({ title: "Sign in to save items" });
      return;
    }
    if (!savedSet.has(itemId)) void toggleSave(itemId);
    fireBurst("heart");
  };
  const onSurfaceClick = useSurfaceDoubleTap(likeFromGesture);
  // The photo opens the viewer on a single tap, so it needs real tap-vs-double
  // discrimination: hold the viewer open just long enough to see a second tap.
  const heroTapRef = useRef<{ t: number; timer: number | null }>({ t: 0, timer: null });
  const handleHeroTap = () => {
    const now = performance.now();
    const hero = heroTapRef.current;
    if (now - hero.t < 300) {
      hero.t = 0;
      if (hero.timer != null) {
        window.clearTimeout(hero.timer);
        hero.timer = null;
      }
      likeFromGesture();
    } else {
      hero.t = now;
      hero.timer = window.setTimeout(() => {
        hero.timer = null;
        setViewerOpen(true);
      }, 300);
    }
  };
  const [showAddCheck, setShowAddCheck] = useState(false);
  const [selectedQty, setSelectedQty] = useState(1);
  const [customResponses, setCustomResponses] = useState<CustomResponse[]>([]);
  const [isQtyPickerOpen, setIsQtyPickerOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [reportListingOpen, setReportListingOpen] = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState<string>("auto");
  const viewerScrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!viewerOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") window.history.back();
    };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKey);
    };
  }, [viewerOpen]);

  // The open viewer is a history entry, so the phone's back button and the
  // edge-back gesture close the photo instead of leaving the listing. Every
  // close path goes through history.back() so the entry is always consumed
  // (pattern: MapToggle).
  useEffect(() => {
    if (!viewerOpen) return;
    window.history.pushState({ ...window.history.state, stoopPhotoViewer: true }, "");
    const onPop = () => setViewerOpen(false);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [viewerOpen]);

  useEffect(() => {
    if (!viewerOpen) return;
    const el = viewerScrollRef.current;
    if (!el) return;
    el.scrollLeft = activeImageIndex * el.clientWidth;
  }, [viewerOpen]);

  useEffect(() => {
    if (!isQtyPickerOpen) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest("[data-qty-picker-root]")) return;
      setIsQtyPickerOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [isQtyPickerOpen]);


  const { data: item, isLoading, isError } = useQuery<ItemDetail>({
    queryKey: ["/api/items", itemId],
    queryFn: async () => {
      const res = await fetch(`/api/items/${itemId}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: Boolean(itemId),
    placeholderData: () => {
      const cached = queryClient.getQueriesData<unknown>({
        queryKey: ["/api/items"],
        exact: false,
      });
      for (const [, value] of cached) {
        if (!Array.isArray(value)) continue;
        const hit = (value as ItemDetail[]).find(
          (entry) => entry && typeof entry === "object" && (entry as ItemDetail).id === itemId,
        );
        if (hit) return hit;
      }
      return undefined;
    },
  });

  // Everything else this maker sells, in the same order their page lists it —
  // same query key as Profile, so arriving from a maker page starts warm.
  const sellerId = (item as any)?.sellerId as string | undefined;
  const { data: sellerItems = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["/api/users/items", sellerId],
    enabled: Boolean(sellerId),
    queryFn: async () => {
      const res = await fetch(`/api/users/${sellerId}/items`, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });
  const siblingIndex = sellerItems.findIndex((entry) => entry.id === itemId);
  const prevSibling = siblingIndex > 0 ? sellerItems[siblingIndex - 1] : null;
  const nextSibling =
    siblingIndex >= 0 && siblingIndex < sellerItems.length - 1 ? sellerItems[siblingIndex + 1] : null;

  // Warm both neighbours so the swipe lands on a rendered listing rather than a
  // spinner. staleTime is Infinity, so a prefetched item stays fresh.
  useEffect(() => {
    for (const sibling of [prevSibling, nextSibling]) {
      if (!sibling) continue;
      queryClient.prefetchQuery({
        queryKey: ["/api/items", sibling.id],
        queryFn: async () => {
          const res = await fetch(`/api/items/${sibling.id}`, { credentials: "include" });
          if (!res.ok) throw new Error(await res.text());
          return res.json();
        },
      });
    }
  }, [prevSibling?.id, nextSibling?.id, queryClient]);

  // Swiping must not stack history: back should leave the maker's listings
  // entirely rather than replay every listing swiped past. Each swipe replaces
  // the current entry, and the first one seats the maker's page underneath if
  // the buyer arrived from somewhere else, so back always reads
  // listing -> maker -> wherever they came from.
  const swipedRef = useRef(false);
  const goToSibling = (target: { id: string } | null) => {
    if (!target) return;
    const href = `/items/${target.id}?back=${encodeURIComponent(backHref)}`;
    const makerHref = sellerId ? `/u/${sellerId}?back=${encodeURIComponent(backHref)}` : null;
    const makerIsBeneath = !makerHref || backHref.startsWith(`/u/${sellerId}`);
    if (!swipedRef.current && !makerIsBeneath && makerHref) {
      setLocation(makerHref, { replace: true });
      setLocation(href);
    } else {
      setLocation(href, { replace: true });
    }
    swipedRef.current = true;
  };

  const {
    rootRef: swipeRootRef,
    swipe,
    swipeBox,
    incoming,
    touchHandlers,
  } = useSwipePager({
    prev: prevSibling,
    next: nextSibling,
    disabled: preview,
    resetKey: itemId,
    onCommit: goToSibling,
  });

  // The component stays mounted across /items/A -> /items/B (same route), so
  // per-item state would otherwise ride along to the next listing.
  useEffect(() => {
    setSelectedQty(1);
    setSelectedUnitId("auto");
    setCustomResponses([]);
    setActiveImageIndex(0);
    setIsQtyPickerOpen(false);
    setShowAddCheck(false);
  }, [itemId]);

  useEffect(() => {
    if (item && !preview) {
      trackEvent("item_view", `/items/${itemId}`, {
        itemId,
        name: (item as any).name,
        price: (item as any).price ? parseFloat((item as any).price) : undefined,
        sellerId: (item as any).sellerId,
        category: (item as any).category,
      });
    }
  }, [item, itemId]);

  const { data: fulfillmentOptions = [] } = useQuery<BuyerFulfillmentOption[]>({
    queryKey: ["/api/items/fulfillment-options", itemId],
    queryFn: async () => {
      const res = await fetch(`/api/items/${itemId}/fulfillment-options`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: Boolean(itemId),
  });

  const { data: cartItemsDetails = [] } = useQuery<CartItemWithDetails[]>({
    queryKey: ["/api/cart/details", JSON.stringify(cart)],
    queryFn: async () => {
      if (cart.length === 0) return [];
      const res = await fetch(`/api/cart/details?cart=${encodeURIComponent(JSON.stringify(cart))}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: cart.length > 0,
  });

  const handleBack = () => {
    // After a swipe the maker's page is the entry below this one; popping keeps
    // the header arrow and the phone's back gesture doing the same thing. With
    // no explicit back target (map card, shared link) popping is also the only
    // exit that keeps what's underneath -- pushing a bare "/" erases the query
    // string (filters) and strands any map entry this page was stacked on.
    if ((swipedRef.current || !backHref) && window.history.length > 1) {
      window.history.back();
      return;
    }
    setLocation(backHref || "/");
  };

  const handleCheckout = () => {
    setIsCartOpen(false);
    setLocation("/checkout");
  };

  const handleShare = async () => {
    if (typeof window === "undefined") return;

    // Branded canonical link with a readable name slug before the id, never
    // window.location.href (the wrapped app loads the *.appspot.com origin).
    const cleanName = stripDemoMarker(item?.name || "") || item?.name || "";
    const slug = itemSlug(cleanName);
    const shareUrl = `https://trystoop.com/items/${slug ? `${slug}-` : ""}${itemId}`;
    const shareText = item?.name
      ? `Check out ${stripDemoMarker(item.name) || item.name} on Stoop`
      : "Check out this item on Stoop";

    try {
      if (navigator.share) {
        await navigator.share({
          title: item?.name || "Stoop item",
          text: shareText,
          url: shareUrl,
        });
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      }
    } catch (error: any) {
      if (error?.name === "AbortError") return;
    }
  };

  const [weightAdding, setWeightAdding] = useState(false);
  const [weightError, setWeightError] = useState<string | null>(null);
  const [showWeightPicker, setShowWeightPicker] = useState(false);
  const { toast } = useToast();

  const addResolvedWeightUnit = (unit: ResolvedWeightUnit) => {
    if (!item) return;
    addToCart(item.id, 1, undefined, undefined, undefined, unit);
    queryClient.invalidateQueries({ queryKey: ["/api/items", itemId] });
    setShowWeightPicker(false);
    setShowAddCheck(true);
    window.setTimeout(() => setShowAddCheck(false), 700);
    toast({
      title: "Added to cart",
      description: `${item.name} · ${unitDisplayLabel({ label: unit.label, weightCentiLb: unit.weight })} · $${(unit.priceCents / 100).toFixed(2)}`,
      duration: 5000,
    });
  };

  const handleAddWeightUnit = async () => {
    if (!item) return;
    setWeightAdding(true);
    setWeightError(null);
    try {
      const res = await fetch("/api/cart/select-weight-unit", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ itemId: item.id, unitId: selectedUnitId }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      addToCart(item.id, 1, undefined, undefined, undefined, data.unit as ResolvedWeightUnit);
      queryClient.invalidateQueries({ queryKey: ["/api/items", itemId] });
      setShowAddCheck(true);
      window.setTimeout(() => setShowAddCheck(false), 700);
    } catch {
      setWeightError("That one just went. Pick another size.");
    } finally {
      setWeightAdding(false);
    }
  };

  const handleAddToCart = () => {
    if (!item) return;
    if (Boolean(item.soldByWeight)) {
      setShowWeightPicker(true);
      return;
    }
    if ((item as any).isMadeToOrder === 1) {
      const fields = ((item as any).customFields as MadeToOrderField[] | null) ?? [];
      if (!madeToOrderResponsesComplete(fields, customResponses)) return;
      const min = Math.max(1, (item as any).minQty ?? 1);
      const qty = Math.max(min, selectedQty);
      addToCart(item.id, qty, undefined, undefined, customResponses);
    } else {
      if (item.qtyAvailable <= 0) return;
      const inCart = cart.find((entry) => entry.itemId === item.id)?.qty ?? 0;
      const remaining = item.qtyAvailable - inCart;
      if (remaining <= 0) return;
      const qty = Math.max(1, Math.min(selectedQty, remaining));
      addToCart(item.id, qty, item.qtyAvailable);
    }
    setShowAddCheck(true);
    setIsQtyPickerOpen(false);
    window.setTimeout(() => setShowAddCheck(false), 700);
  };

  if (isLoading && !item) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        {!preview && <Header cartCount={cartCount} onCartClick={() => setIsCartOpen(true)} />}
        <main className="flex-1">
          <div className="container mx-auto max-w-5xl px-4 py-8">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading product details…
            </div>
          </div>
        </main>
      </div>
    );
  }

  if ((isError && !item) || !item) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        {!preview && <Header cartCount={cartCount} onCartClick={() => setIsCartOpen(true)} />}
        <main className="flex-1">
          <div className="container mx-auto max-w-5xl px-4 py-8">
            <Button variant="outline" size="sm" onClick={handleBack}>
              Back to browse
            </Button>

            <div className="mt-6 rounded-3xl border border-card-border bg-card p-5 shadow-sm">
              <h1 className="text-lg font-semibold">Product not available</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                We could not load this listing. Try returning to browse and opening it again.
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const realShortDescription = item.shortDescription?.trim() || "";
  const realSubtype = item.subtype?.trim() || "";
  const realAttributes = item.attributesJson || {};
  // The `unit` field is the canonical "what you get for the price" now, so when
  // it's set we hide the AI's older bundleSize attribute — otherwise buyers see
  // a conflicting second size descriptor (e.g. unit "Per dozen" vs "half-dozen").
  const bundleSize = String((item as any).unit || "").trim()
    ? ""
    : typeof realAttributes.bundleSize === "string"
      ? realAttributes.bundleSize.trim()
      : "";
  const harvestTiming =
    typeof realAttributes.harvestTiming === "string" ? realAttributes.harvestTiming.trim() : "";
  const quickDetails = Array.isArray((realAttributes as any).quickDetails)
    ? ((realAttributes as any).quickDetails as unknown[])
        .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
        .slice(0, 6)
    : [];

  const price = parseFloat(item.price);
  const sale = saleInfo(item);
  const displayPrice = sale.onSale && sale.salePrice !== null ? sale.salePrice : price;
  const groceryAvg = item.groceryAvg ? parseFloat(item.groceryAvg) : null;
  const savings = groceryAvg && groceryAvg > price ? groceryAvg - price : null;

  const displayItemName = stripDemoMarker(item.name) || item.name;

  const listingImages = getListingImages(item);
  const carouselImages = listingImages;
  const isClothing = isClothingItem(item);
  const clothing = isClothing ? getClothingAttributes(item) : null;
  const sellerName =
    item.seller
      ? stripDemoMarker(
          (item.seller as any).displayName ||
            `${item.seller.firstName || ""} ${item.seller.lastName || ""}`,
        ) || "Local Producer"
      : "Local Producer";

  const sellerTypeLabel = prettyLabel((item.seller as any)?.sellerType ?? null);
  const categoryLabel = prettyLabel(item.category ?? null);
  const subtypeLabel = prettyLabel(realSubtype || null);

  const sellerLatitude =
    (item.seller as any)?.latitude == null || (item.seller as any)?.latitude === ""
      ? null
      : Number((item.seller as any).latitude);
  const sellerLongitude =
    (item.seller as any)?.longitude == null || (item.seller as any)?.longitude === ""
      ? null
      : Number((item.seller as any).longitude);

  const distanceLabel = formatDistanceLabel({
    buyerLocation,
    producerLocation:
      Number.isFinite(sellerLatitude) && Number.isFinite(sellerLongitude)
        ? {
            source: "producer_address",
            label: sellerName,
            zipCode: item.seller?.zipCode ?? item.zipCode ?? null,
            point: {
              latitude: sellerLatitude as number,
              longitude: sellerLongitude as number,
            },
          }
        : null,
    fallbackBuyerZip: buyerLocation?.zipCode?.trim() || null,
    fallbackProducerZip: item.seller?.zipCode ?? item.zipCode ?? null,
  });

  const availableModes = Array.from(
    new Set(fulfillmentOptions.map((option) => option.fulfillmentMethod))
  );

  const modeLabels = {
    seller_pickup: "Pickup from producer",
    drop_zone_pickup: "Community hub",
    local_delivery: "Local delivery",
  } as const;

  const isMadeToOrder = (item as any).isMadeToOrder === 1;
  const requiresDeposit = (item as any).requiresDeposit === 1;
  const customFields = ((item as any).customFields as MadeToOrderField[] | null) ?? [];
  const minQty = Math.max(1, (item as any).minQty ?? 1);
  const leadTimeDays = (item as any).leadTimeDays ?? null;
  // Earliest pickup for a made-to-order item: the lead time, then snapped
  // FORWARD to the seller's next actual pickup day, so we never advertise a
  // date on a day the seller isn't open. (A maker whose pickups are Wed/Thu
  // saw the raw 3-day lead land on a Friday, contradicting her pickup days.)
  const allowedPickupDays = (() => {
    const set = new Set<number>();
    const names = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
    const scan = (text?: string | null) => {
      const t = (text || "").toLowerCase();
      names.forEach((n, i) => {
        if (t.includes(n)) set.add(i);
      });
    };
    for (const opt of fulfillmentOptions) {
      (opt.timeSlots ?? []).forEach((slot) => scan(slot.dayOfWeek));
      scan(opt.dayLabel);
    }
    return set;
  })();
  const mtoReadyDate = (() => {
    if (!isMadeToOrder || !leadTimeDays) return null;
    const d = new Date(Date.now() + leadTimeDays * 86_400_000);
    d.setHours(0, 0, 0, 0);
    if (allowedPickupDays.size > 0 && allowedPickupDays.size < 7) {
      for (let i = 0; i < 14 && !allowedPickupDays.has(d.getDay()); i++) {
        d.setDate(d.getDate() + 1);
      }
    }
    return d;
  })();

  const inCartQty = cart.find((entry) => entry.itemId === item.id)?.qty ?? 0;
  const remainingStock = isMadeToOrder ? 99 : Math.max(0, item.qtyAvailable - inCartQty);
  const atStockCap = isMadeToOrder ? false : item.qtyAvailable > 0 && remainingStock <= 0;
  const maxQty = isMadeToOrder ? 99 : Math.max(1, remainingStock || 1);
  const safeSelectedQty = Math.min(Math.max(selectedQty, isMadeToOrder ? minQty : 1), maxQty);
  if (safeSelectedQty !== selectedQty) {
    setSelectedQty(safeSelectedQty);
  }
  const soldOut = !isMadeToOrder && item.qtyAvailable <= 0;
  const addDisabled = isMadeToOrder
    ? !madeToOrderResponsesComplete(customFields, customResponses)
    : (soldOut || atStockCap);

  const isGrowing = (item as any).growthState === "growing";
  const isMeatShare = Number((item as any).isMeatShare ?? 0) === 1;
  const ripeDate = (item as any).estimatedRipeDate
    ? new Date((item as any).estimatedRipeDate)
    : null;
  // Meat shares are paid in full up front (the processing fee is charged later,
  // on the slaughter date), so the "reserve now" amount is the whole share price.
  const depositPerUnit = isMeatShare ? price : price * 0.25;

  const soldByWeight = Boolean(item.soldByWeight);
  const availableUnits = (item.availableUnits ?? []) as WeightUnitOption[];
  const weightSoldOut = soldByWeight && (item.unitsAvailable ?? 0) <= 0;
  const selectedUnit =
    selectedUnitId === "auto"
      ? null
      : availableUnits.find((u) => u.id === selectedUnitId) ?? null;
  const namedUnits = unitsAreNamed(availableUnits);
  const unitChoices = collapseUnitChoices(availableUnits);
  const autoUnit = (() => {
    if (availableUnits.length === 0) return null;
    // Named options aren't a range, so there is no "middle" one to grab. With a
    // single option we can still preselect it; with several the buyer must pick.
    if (namedUnits) return unitChoices.length === 1 ? unitChoices[0].unit : null;
    const sorted = [...availableUnits].sort((a, b) => a.weight - b.weight);
    return sorted[Math.floor(sorted.length / 2)];
  })();
  const activeUnit = selectedUnit ?? autoUnit;
  const weightRangeLabel =
    soldByWeight && !namedUnits && item.weightMin != null && item.weightMax != null
      ? item.weightMin === item.weightMax
        ? formatWeight(item.weightMin)
        : `${formatWeight(item.weightMin)}–${formatWeight(item.weightMax)}`
      : null;
  const weightPriceRangeLabel = (() => {
    const perLb = (item as any).pricePerLbCents as number | null | undefined;
    if (!soldByWeight || item.weightMin == null || item.weightMax == null || !perLb) return null;
    const minP = priceForUnit(item.weightMin, perLb) / 100;
    const maxP = priceForUnit(item.weightMax, perLb) / 100;
    return minP === maxP
      ? `$${minP.toFixed(2)}`
      : `$${minP.toFixed(2)}–$${maxP.toFixed(2)}`;
  })();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {!preview && <Header cartCount={cartCount} onCartClick={() => setIsCartOpen(true)} />}

      <div className="flex flex-1 flex-col overflow-x-hidden">
      <main
        ref={swipeRootRef}
        className="flex-1"
        onClick={preview ? undefined : onSurfaceClick}
        {...touchHandlers}
        style={
          swipe.dx !== 0 || swipe.animating
            ? {
                transform: `translate3d(${swipe.dx}px, 0, 0)`,
                transition: swipe.animating ? "transform 180ms ease-out" : "none",
                willChange: "transform",
              }
            : undefined
        }
      >
        <div className="relative h-48 w-full overflow-hidden sm:h-72">
          <div
            className="flex h-full w-full snap-x snap-mandatory overflow-x-auto"
            onScroll={(e) => {
              const el = e.currentTarget;
              const slideWidth = el.clientWidth || 1;
              const next = Math.round(el.scrollLeft / slideWidth);
              if (next !== activeImageIndex) setActiveImageIndex(next);
            }}
          >
            {carouselImages.length === 0 ? (
              <ListingPhotoPlaceholder category={item.category} />
            ) : (
              carouselImages.map((url, i) => (
                <img
                  key={`${url}-${i}`}
                  src={url}
                  alt={carouselImages.length > 1 ? `${displayItemName} — photo ${i + 1}` : displayItemName}
                  className="h-full w-full shrink-0 cursor-zoom-in snap-center object-cover"
                  loading={i === 0 ? "eager" : "lazy"}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleHeroTap();
                  }}
                />
              ))
            )}
          </div>
          <div className="pointer-events-none absolute inset-0 bg-image-overlay-dark" />

          <button
            type="button"
            onClick={() => setViewerOpen(true)}
            aria-label="View photos fullscreen"
            className="absolute right-3 top-14 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur transition hover:bg-black/70 sm:top-16"
          >
            <Maximize2 className="h-4 w-4" />
          </button>

          {carouselImages.length > 1 && (
            <>
              <div className="pointer-events-none absolute bottom-12 left-1/2 z-20 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-black/35 px-2.5 py-1.5 backdrop-blur sm:bottom-20">
                {carouselImages.map((_, i) => (
                  <span
                    key={i}
                    className={`h-1.5 rounded-full transition-all ${
                      i === activeImageIndex ? "w-4 bg-white" : "w-1.5 bg-white/70"
                    }`}
                  />
                ))}
              </div>
              <div className="pointer-events-none absolute bottom-12 right-3 z-20 rounded-full bg-black/50 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur sm:bottom-20 sm:right-4">
                {Math.min(activeImageIndex + 1, carouselImages.length)} / {carouselImages.length}
              </div>
            </>
          )}

          <div className="absolute inset-x-0 top-3 z-20 px-4 sm:top-4">
            <div className="container mx-auto max-w-5xl">
              <div className="flex items-center justify-between gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 rounded-full border-white/70 bg-background/90 shadow-sm backdrop-blur sm:h-9 sm:w-9"
                  onClick={handleBack}
                  aria-label="Go back"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>

                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full border-white/70 bg-background/90 shadow-sm backdrop-blur sm:h-9 sm:w-9"
                    onClick={() => {
                      if (!authedUser) {
                        toast({ title: "Sign in to save items" });
                        return;
                      }
                      const wasSaved = savedSet.has(itemId);
                      void toggleSave(itemId);
                      if (!wasSaved) fireBurst("heart");
                    }}
                    aria-label={isSaved ? "Unsave" : "Save"}
                  >
                    <Heart className={`h-4 w-4 ${isSaved ? "fill-red-500 text-red-500" : ""}`} />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full border-white/70 bg-background/90 shadow-sm backdrop-blur sm:h-9 sm:w-9"
                    onClick={handleShare}
                    aria-label="Share product"
                  >
                    <Share2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {(item as any).growthState === "growing" && (item as any).estimatedRipeDate ? (
            <div className="absolute bottom-3 left-4 z-20 sm:bottom-4">
              <div className="container mx-auto max-w-5xl">
                <Badge className="border-0 bg-emerald-600 text-white shadow-sm">
                  <Sprout className="mr-1 h-3.5 w-3.5" />
                  Reserve — ready ~
                  {new Date((item as any).estimatedRipeDate).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </Badge>
              </div>
            </div>
          ) : item.freshnessNote && item.qtyAvailable > 0 && !isMadeToOrder ? (
            <div className="absolute bottom-3 left-4 right-4 z-20 sm:bottom-4">
              <div className="container mx-auto max-w-5xl">
                <Badge className="max-w-full border-0 bg-primary/92 text-primary-foreground shadow-sm">
                  <span className="line-clamp-1">{item.freshnessNote}</span>
                </Badge>
              </div>
            </div>
          ) : null}
        </div>

        <div className="container mx-auto max-w-5xl px-4 pb-32 md:pb-8">
          <div className="relative z-10 -mt-8 mb-5 sm:-mt-16">
            <div className="grid gap-4">
              <div className="rounded-[1.6rem] border border-primary/15 bg-card-gradient p-4 shadow-sm sm:rounded-[2rem] sm:p-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div className="min-w-0">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                        {subtypeLabel || categoryLabel || "Local listing"}
                      </span>

                      {categoryLabel && (
                        <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 text-[11px] font-medium text-foreground">
                          <Sprout className="mr-1 h-3.5 w-3.5 text-primary" />
                          {categoryLabel}
                        </span>
                      )}

                      {subtypeLabel && (
                        <span className="inline-flex items-center rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-medium text-foreground">
                          {subtypeLabel}
                        </span>
                      )}

                      {(item as any).isGlutenFree === 1 && (
                        <span className="inline-flex items-center rounded-full border-0 bg-amber-500 px-2.5 py-1 text-[11px] font-medium text-white">
                          Gluten-free
                        </span>
                      )}

                      <HeatBadge level={(item as any).heatLevel} showScoville className="px-2.5 py-1 text-[11px]" />
                    </div>

                    <h1 className="max-w-[16ch] text-[1.7rem] font-bold leading-[1.02] tracking-tight sm:max-w-none sm:text-4xl">
                      {displayItemName}
                    </h1>

                    <div className="mt-2.5 flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
                      <span className="inline-flex items-center rounded-full border border-border bg-card px-2 py-0.5 text-[11px] font-medium text-foreground sm:px-2.5 sm:py-1 sm:text-sm">
                        {item.qtyAvailable > 0 ? `${item.qtyAvailable} available` : "Out of stock"}
                      </span>

                      {distanceLabel && (
                        <span className="inline-flex items-center rounded-full border border-border bg-card px-2 py-0.5 text-[11px] font-medium text-foreground sm:px-2.5 sm:py-1 sm:text-sm">
                          {distanceLabel}
                        </span>
                      )}

                      {item.zipCode && (
                        <span className="inline-flex items-center rounded-full border border-border bg-card px-2 py-0.5 text-[11px] font-medium text-foreground sm:px-2.5 sm:py-1 sm:text-sm">
                          <MapPin className="mr-1 h-3.5 w-3.5 text-primary" />
                          {item.zipCode}
                        </span>
                      )}
                    </div>

                    <div className="mt-3 text-sm leading-6 text-muted-foreground">
                      {realShortDescription ||
                        (bundleSize || harvestTiming || quickDetails.length > 0
                          ? [bundleSize, harvestTiming, quickDetails.slice(0, 2).join(" • ")]
                              .filter(Boolean)
                              .join(" • ")
                          : `${displayItemName} from ${sellerName}.`)}
                    </div>
                  </div>

                  <div className="sm:text-right">
                    <div className="flex flex-wrap items-baseline gap-2 sm:justify-end">
                      <span className="text-3xl font-bold text-primary sm:text-4xl">
                        {weightPriceRangeLabel ?? `$${displayPrice.toFixed(2)}`}
                      </span>
                      {sale.onSale && (
                        <>
                          <span className="text-lg font-medium text-muted-foreground line-through">
                            ${price.toFixed(2)}
                          </span>
                          <span className="rounded-full bg-primary px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary-foreground">
                            Sale
                          </span>
                        </>
                      )}
                    </div>
                    {(item as any).unit && (
                      <div className="text-sm font-medium text-muted-foreground">
                        {(item as any).unit}
                      </div>
                    )}
                    {(item as any).bulkDiscountThresholdCents != null &&
                      Number((item as any).bulkDiscountPercent) > 0 && (
                        <div className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                          Spend ${((item as any).bulkDiscountThresholdCents / 100).toFixed(0)}+ with
                          this seller, save {(item as any).bulkDiscountPercent}%
                        </div>
                      )}

                    <div className="mt-2 flex flex-wrap items-center gap-2 sm:justify-end">
                      {groceryAvg && (
                        <span className="text-sm text-muted-foreground line-through">
                          Avg ${groceryAvg.toFixed(2)}
                        </span>
                      )}

                      {savings && savings > 0 && (
                        <Badge
                          variant="outline"
                          className="border-chart-2/30 bg-chart-2/10 text-chart-2"
                        >
                          Save ${savings.toFixed(2)}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2.5">
                  {item.seller?.id && (
                    <Link
                      href={`/u/${item.seller.id}?back=${encodeURIComponent(`/items/${item.id}?back=${encodeURIComponent(backHref)}`)}`}
                      className="inline-flex items-center gap-2.5 rounded-full border border-primary/15 bg-background/95 px-2.5 py-1.5 shadow-sm transition hover:bg-background"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-muted">
                        {item.seller?.profileImageUrl ? (
                          <img
                            src={item.seller.profileImageUrl}
                            alt={sellerName}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="text-xs font-semibold text-muted-foreground">
                            {sellerName.slice(0, 2).toUpperCase()}
                          </div>
                        )}
                      </div>

                      <div className="min-w-0 text-left">
                        <div className="truncate text-[13px] font-semibold text-foreground sm:text-sm">
                          {sellerName}
                        </div>
                        <div className="truncate text-[11px] text-muted-foreground">
                          {[sellerTypeLabel, distanceLabel].filter(Boolean).join(" • ") || "View profile"}
                        </div>
                      </div>
                    </Link>
                  )}

                  {item.seller?.id && (
                    (item.seller.reviewCount ?? 0) > 0 ? (
                      <Link
                        href={`/u/${item.seller.id}?back=${encodeURIComponent(`/items/${item.id}?back=${encodeURIComponent(backHref)}`)}#reviews`}
                        className="inline-flex items-center gap-1 rounded-full border border-amber-200/70 bg-amber-50 px-2.5 py-1.5 text-sm font-medium text-amber-700 shadow-sm hover:bg-amber-100"
                        data-testid="seller-rating-detail"
                      >
                        <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />
                        <span className="font-semibold">{(item.seller.averageRating ?? 0).toFixed(1)}</span>
                        <span className="text-xs text-muted-foreground">· {item.seller.reviewCount} review{item.seller.reviewCount === 1 ? "" : "s"}</span>
                      </Link>
                    ) : (
                      <Link
                        href={`/u/${item.seller.id}?back=${encodeURIComponent(`/items/${item.id}?back=${encodeURIComponent(backHref)}`)}#reviews`}
                        className="inline-flex items-center rounded-full border border-primary/25 bg-primary/10 px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-primary shadow-sm"
                        data-testid="seller-rating-new-detail"
                      >
                        New seller
                      </Link>
                    )
                  )}
                </div>

              </div>
            </div>
          </div>

          {isClothing && clothing && (
            <Card className="mb-5 overflow-hidden border-primary/20 bg-card-gradient">
              <CardContent className="space-y-4 pt-5">
                <div className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-primary">
                  <Sprout className="h-3.5 w-3.5" />
                  1 of 1 — when it's gone, it's gone
                </div>

                {(clothing.garmentType ||
                  clothing.size ||
                  clothing.condition ||
                  clothing.color) && (
                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: "Type", value: garmentLabel(clothing.garmentType) || clothing.garmentType },
                      { label: "Size", value: clothing.size },
                      { label: "Condition", value: conditionLabel(clothing.condition) || clothing.condition },
                      { label: "Color", value: clothing.color },
                    ]
                      .filter((field) => Boolean(field.value))
                      .map((field) => (
                        <div
                          key={field.label}
                          className="rounded-full border border-border bg-background/70 px-3 py-1.5 text-sm"
                        >
                          <span className="text-muted-foreground">{field.label}: </span>
                          <span className="font-semibold text-foreground">{field.value}</span>
                        </div>
                      ))}
                  </div>
                )}

                {clothing.measurements &&
                  (clothing.measurements.chest ||
                    clothing.measurements.length ||
                    clothing.measurements.waist ||
                    clothing.measurements.inseam) && (
                    <div>
                      <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        Measurements
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {[
                          { label: "Pit to pit", value: clothing.measurements.chest },
                          { label: "Length", value: clothing.measurements.length },
                          { label: "Waist", value: clothing.measurements.waist },
                          { label: "Inseam", value: clothing.measurements.inseam },
                        ]
                          .filter((m) => Boolean(m.value))
                          .map((m) => {
                            const raw = String(m.value);
                            const display = /[a-zA-Z]/.test(raw) ? raw : `${raw} in`;
                            return (
                              <div
                                key={m.label}
                                className="flex items-center justify-between rounded-2xl border border-border/70 bg-background/70 px-3.5 py-2.5"
                              >
                                <span className="text-sm text-muted-foreground">{m.label}</span>
                                <span className="text-sm font-semibold text-foreground">{display}</span>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                {clothing.vibes && clothing.vibes.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {clothing.vibes.map((vibe) => (
                      <span
                        key={vibe}
                        className="inline-flex items-center rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground"
                      >
                        #{vibe}
                      </span>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {isMadeToOrder && (
            <Card className="mb-5 overflow-hidden border-primary/30 bg-primary/5">
              <CardContent className="pt-5 space-y-3">
                <div className="flex items-center gap-2">
                  <ShoppingBasket className="h-4 w-4 text-primary" />
                  <div className="text-sm font-semibold uppercase tracking-[0.14em] text-primary">
                    Made to order
                  </div>
                </div>
                <p className="text-sm leading-6 text-muted-foreground">
                  {sellerName} makes this to order
                  {leadTimeDays ? ` — ready in about ${leadTimeDays} day${leadTimeDays === 1 ? "" : "s"}` : ""}.
                  {mtoReadyDate ? (
                    <>
                      {" "}If you order today, the earliest pickup is{" "}
                      <span className="font-semibold text-foreground">
                        {mtoReadyDate.toLocaleDateString(undefined, {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                      .
                    </>
                  ) : null}
                  {minQty > 1 ? ` Minimum ${minQty}.` : ""}{" "}
                  {requiresDeposit
                    ? `Pay a 25% deposit now; the balance is charged when it's ready. You'll add your request on the next step.`
                    : `You pay now; ${sellerName} confirms the details with you in your order chat.`}
                </p>
                {!requiresDeposit && customFields.length > 0 && (
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                      Customize your order
                    </label>
                    <MadeToOrderFields
                      fields={customFields}
                      responses={customResponses}
                      onChange={setCustomResponses}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {isGrowing && (
            <Card className="mb-5 overflow-hidden border-emerald-300/60 bg-emerald-50/40">
              <CardContent className="pt-5 space-y-3">
                <div className="flex items-center gap-2">
                  <Sprout className="h-4 w-4 text-emerald-700" />
                  <div className="text-sm font-semibold uppercase tracking-[0.14em] text-emerald-900">
                    Reserve on the vine
                  </div>
                </div>
                <p className="text-sm leading-6 text-emerald-900/80">
                  This item is still growing. Pay a 25% deposit to reserve it now —
                  the remaining 75% auto-charges on the ripe date.
                  {ripeDate && (
                    <>
                      {" "}Estimated ready{" "}
                      <span className="font-semibold">
                        {ripeDate.toLocaleDateString(undefined, {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                      .
                    </>
                  )}
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="rounded-lg bg-white/80 p-2.5">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-800/70">
                      Deposit (today)
                    </div>
                    <div className="font-semibold text-emerald-900">
                      ${(depositPerUnit * safeSelectedQty).toFixed(2)}
                    </div>
                  </div>
                  <div className="rounded-lg bg-white/80 p-2.5">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-800/70">
                      Balance (on ripe)
                    </div>
                    <div className="font-semibold text-emerald-900">
                      ${(price * safeSelectedQty * 0.75).toFixed(2)}
                    </div>
                  </div>
                </div>
                <p className="text-[11px] text-emerald-900/70">
                  Free cancel for 48 hours after reserving — full refund.
                  After that, cancelling forfeits the 25% deposit (the 75% balance never charges).
                  If the seller cancels or the crop fails, your full deposit is refunded.
                </p>
                <GrowthPhotosStrip itemId={item.id} />
              </CardContent>
            </Card>
          )}

          <Card className="mb-5 overflow-hidden border-primary/15 bg-card-gradient">
            <CardContent className="pt-5">
              <div className="mb-2 text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                How to get this item
              </div>

              {fulfillmentOptions.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {availableModes.map((mode) => (
                      <span
                        key={mode}
                        className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-sm font-medium text-foreground"
                      >
                        {modeLabels[mode]}
                      </span>
                    ))}
                  </div>

                  <div className="grid gap-2.5">
                    {fulfillmentOptions.map((option) => (
                      <div
                        key={option.id}
                        className="rounded-2xl border border-primary/20 bg-primary/10 p-3.5"
                      >
                        <div className="flex items-start gap-3">
                          {option.fulfillmentMethod === "local_delivery" ? (
                            <Truck className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                          ) : (
                            <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                          )}

                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-foreground">
                              {option.buyerLabel}
                            </div>
                            <div className="mt-1 text-sm leading-6 text-muted-foreground">
                              {option.description?.trim() ||
                                option.dayLabel?.trim() ||
                                "Pickup or delivery details will be shared by the seller."}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-primary/20 bg-primary/10 p-4 text-sm leading-6 text-muted-foreground">
                  No fulfillment details have been added yet.
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <Card className="border-primary/15 bg-card-gradient">
              <CardContent className="pt-5">
                <div className="text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  Product details
                </div>

                <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
                  {(bundleSize || harvestTiming || quickDetails.length > 0) ? (
                    <>
                      {bundleSize && (
                        <div className="rounded-2xl border border-border/70 bg-background/70 p-3.5">
                          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                            Bundle size
                          </div>
                          <div className="mt-1 text-sm leading-6 text-muted-foreground">
                            {bundleSize}
                          </div>
                        </div>
                      )}

                      {harvestTiming && (
                        <div className="rounded-2xl border border-border/70 bg-background/70 p-3.5">
                          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                            Harvest timing
                          </div>
                          <div className="mt-1 text-sm leading-6 text-muted-foreground">
                            {harvestTiming}
                          </div>
                        </div>
                      )}

                      {quickDetails.length > 0 && (
                        <div className="rounded-2xl border border-border/70 bg-background/70 p-3.5 sm:col-span-2">
                          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                            Quick details
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {quickDetails.map((detail) => (
                              <span
                                key={detail}
                                className="inline-flex items-center rounded-full border border-border bg-card px-3 py-1 text-sm font-medium text-foreground"
                              >
                                {detail}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="rounded-2xl border border-border/70 bg-background/70 p-3.5 sm:col-span-2">
                      <div className="text-sm leading-6 text-muted-foreground">
                        No additional details yet.
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-primary/15 bg-card-gradient">
              <CardContent className="pt-5">
                <div className="text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  Seller snapshot
                </div>

                <div className="mt-3 space-y-2.5">
                  <div className="rounded-2xl border border-border/70 bg-background/70 p-3.5">
                    <div className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                      Seller
                    </div>
                    <div className="mt-1 text-sm font-semibold text-foreground">
                      {sellerName}
                    </div>
                    <div className="mt-1 text-sm leading-6 text-muted-foreground">
                      {[sellerTypeLabel, distanceLabel, item.zipCode].filter(Boolean).join(" • ") || "Local producer"}
                    </div>
                  </div>

                  {soldByWeight ? (
                    <div className="rounded-2xl border border-border/70 bg-background/70 p-3.5">
                      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        Choose your size
                      </div>
                      <div className="mt-1 text-sm leading-6 text-muted-foreground">
                        {weightSoldOut
                          ? "All sold — check back soon"
                          : namedUnits
                            ? "Pick the size you want."
                            : `Each is weighed individually${weightRangeLabel ? ` · ${weightRangeLabel}` : ""}. Priced at its exact weight.`}
                      </div>
                      {!weightSoldOut && (
                        <div className="mt-2.5 grid grid-cols-2 gap-1.5">
                          {!namedUnits && (
                          <button
                            type="button"
                            onClick={() => setSelectedUnitId("auto")}
                            className={`col-span-2 rounded-xl border px-3 py-2 text-left text-sm transition ${
                              selectedUnitId === "auto"
                                ? "border-primary bg-primary/10 font-semibold text-foreground"
                                : "border-card-border bg-card text-muted-foreground"
                            }`}
                            data-testid="weight-select-auto"
                          >
                            Pick one for me
                            {autoUnit && (
                              <span className="ml-1 text-xs text-muted-foreground">
                                (~{formatWeight(autoUnit.weight)})
                              </span>
                            )}
                          </button>
                          )}
                          {unitChoices.map(({ unit, remaining }) => (
                            <button
                              key={unit.id}
                              type="button"
                              onClick={() => setSelectedUnitId(unit.id)}
                              className={`flex items-center justify-between gap-1 rounded-xl border px-3 py-2 text-sm transition ${
                                selectedUnitId === unit.id
                                  ? "border-primary bg-primary/10 font-semibold text-foreground"
                                  : "border-card-border bg-card text-muted-foreground"
                              }`}
                              data-testid={`weight-select-unit-${unit.id}`}
                            >
                              <span className="truncate">
                                {unitDisplayLabel({ label: unit.label, weightCentiLb: unit.weight })}
                                {remaining > 1 && (
                                  <span className="ml-1 text-xs font-normal text-muted-foreground">
                                    ×{remaining}
                                  </span>
                                )}
                              </span>
                              <span className="text-primary">
                                ${(unit.priceCents / 100).toFixed(2)}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                      {activeUnit && !weightSoldOut && (
                        <div className="mt-2.5 flex items-baseline justify-between border-t border-border/60 pt-2">
                          <span className="text-xs text-muted-foreground">
                            {selectedUnit
                              ? unitDisplayLabel({ label: selectedUnit.label, weightCentiLb: selectedUnit.weight })
                              : namedUnits
                                ? unitDisplayLabel({ label: activeUnit.label, weightCentiLb: activeUnit.weight })
                                : `~${formatWeight(activeUnit.weight)}`}
                          </span>
                          <span className="text-base font-bold text-primary">
                            ${(activeUnit.priceCents / 100).toFixed(2)}
                          </span>
                        </div>
                      )}
                      {weightError && (
                        <p className="mt-2 text-xs text-destructive" role="alert">
                          {weightError}
                        </p>
                      )}
                      <Button
                        className="mt-3 w-full rounded-full"
                        onClick={handleAddWeightUnit}
                        disabled={weightSoldOut || weightAdding || (namedUnits && !activeUnit)}
                        data-testid="button-add-weight-unit"
                      >
                        {weightAdding ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : showAddCheck ? (
                          <>
                            <Check className="mr-1 h-4 w-4" /> Added
                          </>
                        ) : weightSoldOut ? (
                          "Sold out"
                        ) : activeUnit ? (
                          `Add to cart · $${(activeUnit.priceCents / 100).toFixed(2)}`
                        ) : namedUnits ? (
                          "Choose a size"
                        ) : (
                          "Add to cart"
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-border/70 bg-background/70 p-3.5">
                      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        Availability
                      </div>
                      <div className="mt-1 text-sm leading-6 text-muted-foreground">
                        {item.qtyAvailable <= 0
                          ? "Currently out of stock"
                          : atStockCap
                            ? `Max in cart (${item.qtyAvailable} available)`
                            : `${item.qtyAvailable} available now`}
                      </div>
                    </div>
                  )}

                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-card-border bg-background/95 px-3 pb-[calc(60px+env(safe-area-inset-bottom)+0.6rem)] pt-2 shadow-[0_-10px_30px_rgba(0,0,0,0.08)] backdrop-blur md:hidden">
          {soldByWeight ? (
          <div className="mx-auto flex max-w-5xl items-center justify-between gap-2">
            <div className="min-w-0 shrink rounded-full border border-card-border bg-card px-3 py-2">
              <div className="flex items-baseline gap-1.5 leading-none">
                <span className="text-base font-bold text-primary">
                  {weightSoldOut ? "Sold out" : `from $${parseFloat(item.price).toFixed(2)}`}
                </span>
              </div>
              {!weightSoldOut && weightRangeLabel && (
                <div className="mt-1 truncate text-[10px] leading-none text-muted-foreground">
                  {weightRangeLabel}
                </div>
              )}
            </div>
            <Button
              size="lg"
              className="h-10 min-w-[140px] rounded-full px-3 text-sm font-semibold"
              onClick={handleAddToCart}
              disabled={weightSoldOut}
              data-mission-target="add-to-cart"
            >
              {showAddCheck ? (
                <>
                  <Check className="mr-1 h-4 w-4" /> Added
                </>
              ) : weightSoldOut ? (
                "Sold out"
              ) : (
                <>
                  <ShoppingBasket className="mr-1 h-4 w-4" />
                  Choose size
                </>
              )}
            </Button>
          </div>
          ) : (
          <div className="mx-auto flex max-w-5xl items-center justify-between gap-2">
            <div className="shrink-0 rounded-full border border-card-border bg-card px-3 py-2">
              <div className="flex items-baseline gap-1.5 leading-none">
                <span className="text-base font-bold text-primary">
                  ${displayPrice.toFixed(2)}
                </span>
                {sale.onSale && (
                  <span className="text-[11px] font-medium text-muted-foreground line-through">
                    ${price.toFixed(2)}
                  </span>
                )}
              </div>
              <div className="mt-1 text-[10px] leading-none text-muted-foreground">
                {(item as any).unit ? `${(item as any).unit} · ` : ""}
                {isMadeToOrder
                  ? "Made to order"
                  : item.qtyAvailable <= 0
                  ? "Out"
                  : atStockCap
                    ? "Max in cart"
                    : `${item.qtyAvailable} avail`}
              </div>
            </div>

            <div className="relative shrink-0" data-qty-picker-root>
              <Button
                type="button"
                variant="outline"
                className="h-10 min-w-[68px] rounded-full px-2.5 text-sm font-semibold"
                onClick={() => remainingStock > 0 && setIsQtyPickerOpen((open) => !open)}
                disabled={soldOut || atStockCap}
              >
                {safeSelectedQty}
                <ChevronDown className="ml-1 h-3.5 w-3.5" />
              </Button>

              {isQtyPickerOpen && remainingStock > 0 && (
                <div className="absolute bottom-[calc(100%+0.45rem)] left-0 w-[160px] rounded-2xl border border-card-border bg-background p-3 shadow-lg">
                  <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Quantity
                  </div>

                  <div className="flex items-center justify-between rounded-full border border-card-border bg-card px-2 py-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full"
                      onClick={() => setSelectedQty((qty) => Math.max(1, qty - 1))}
                      disabled={safeSelectedQty <= 1}
                      aria-label="Decrease quantity"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>

                    <div className="min-w-[3ch] text-center text-base font-semibold text-foreground">
                      {safeSelectedQty}
                    </div>

                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full"
                      onClick={() => setSelectedQty((qty) => Math.min(maxQty, qty + 1))}
                      disabled={safeSelectedQty >= maxQty}
                      aria-label="Increase quantity"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {(isGrowing || isMeatShare || (isMadeToOrder && requiresDeposit)) ? (
              <Button
                size="lg"
                className="h-10 min-w-[140px] rounded-full bg-emerald-600 px-3 text-sm font-semibold text-white hover:bg-emerald-700"
                onClick={() => setLocation(`/reserve/${item.id}?qty=${safeSelectedQty}`)}
                disabled={soldOut || atStockCap}
              >
                <Sprout className="mr-1 h-4 w-4" />
                {isMeatShare ? "Reserve share" : isMadeToOrder ? "Order" : "Reserve"} · $
                {(depositPerUnit * safeSelectedQty).toFixed(2)}
              </Button>
            ) : (item.seller as any)?.isDemoSeller ? (
              <span className="inline-flex h-10 items-center rounded-full bg-muted px-4 text-sm font-medium text-muted-foreground">
                Sample listing
              </span>
            ) : (
              <Button
                size="lg"
                className="h-10 min-w-[108px] rounded-full px-3 text-sm font-semibold"
                onClick={handleAddToCart}
                disabled={addDisabled}
                data-mission-target="add-to-cart"
              >
                {showAddCheck ? (
                  <>
                    <Check className="mr-1 h-4 w-4" />
                    Added
                  </>
                ) : (
                  <>
                    <ShoppingBasket className="mr-1 h-4 w-4" />
                    Add {safeSelectedQty}
                  </>
                )}
              </Button>
            )}
          </div>
          )}
        </div>

        <div className="mx-auto max-w-3xl px-4 pb-4">
          <BarterPanel item={item as any} />
        </div>

        {siblingIndex >= 0 && sellerItems.length > 1 && (
          <div className="mx-auto max-w-3xl px-4 pb-6">
            <div className="flex items-center gap-2 rounded-2xl border border-border/70 bg-background/70 p-2">
              <button
                type="button"
                onClick={() => goToSibling(prevSibling)}
                className={`flex min-w-0 flex-1 items-center gap-1.5 rounded-xl px-2.5 py-2 text-left text-xs transition ${
                  prevSibling ? "text-foreground hover:bg-muted" : "invisible"
                }`}
                data-testid="button-prev-listing"
              >
                <ChevronLeft className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{prevSibling?.name}</span>
              </button>
              <span className="shrink-0 px-1 text-[11px] text-muted-foreground">
                {siblingIndex + 1} of {sellerItems.length}
              </span>
              <button
                type="button"
                onClick={() => goToSibling(nextSibling)}
                className={`flex min-w-0 flex-1 items-center justify-end gap-1.5 rounded-xl px-2.5 py-2 text-right text-xs transition ${
                  nextSibling ? "text-foreground hover:bg-muted" : "invisible"
                }`}
                data-testid="button-next-listing"
              >
                <span className="truncate">{nextSibling?.name}</span>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            </div>
            <p className="mt-1.5 text-center text-[11px] text-muted-foreground">
              Swipe to see more from {sellerName}
            </p>
          </div>
        )}

        <div className="mx-auto max-w-3xl px-4 pb-6 text-center">
          <button
            type="button"
            onClick={() => setReportListingOpen(true)}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground/70 hover:text-muted-foreground"
          >
            <Flag className="h-3.5 w-3.5" /> Report this listing
          </button>
        </div>
      </main>
      </div>

      {!preview && incoming && swipeBox.width > 0 && (
        <div
          className="pointer-events-none fixed inset-x-0 bottom-0 z-30 overflow-hidden"
          style={{ top: swipeBox.top }}
          aria-hidden="true"
        >
          <div
            className="h-full w-full overflow-hidden bg-background"
            style={{
              transform: `translate3d(${
                swipe.dx + (incoming.fromRight ? swipeBox.width : -swipeBox.width)
              }px, 0, 0)`,
              transition: swipe.animating ? "transform 180ms ease-out" : "none",
              willChange: "transform",
            }}
          >
            <ListingScreen itemId={incoming.id} backHref={backHref} preview />
          </div>
        </div>
      )}

      {!preview && <TapBurst burst={burst} onDone={clearBurst} />}

      {!preview && (
      <>
      <ReportDialog
        open={reportListingOpen}
        onOpenChange={setReportListingOpen}
        targetType="listing"
        targetId={item.id}
        targetLabel="listing"
      />

      <CartDrawer
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        items={cartItemsDetails}
        onUpdateQuantity={updateQuantity}
        onRemove={removeFromCart}
        onClearCart={clearCart}
        onCheckout={handleCheckout}
      />

      {viewerOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/95"
          onClick={() => window.history.back()}
        >
          <button
            type="button"
            onClick={() => window.history.back()}
            aria-label="Close"
            className="absolute right-4 top-[calc(env(safe-area-inset-top)+1rem)] z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/20"
          >
            <X className="h-5 w-5" />
          </button>

          <div
            ref={viewerScrollRef}
            className="flex h-full w-full snap-x snap-mandatory overflow-x-auto"
            onClick={(e) => e.stopPropagation()}
            onScroll={(e) => {
              const el = e.currentTarget;
              const slideWidth = el.clientWidth || 1;
              const next = Math.round(el.scrollLeft / slideWidth);
              if (next !== activeImageIndex) setActiveImageIndex(next);
            }}
          >
            {carouselImages.map((url, i) => (
              <img
                key={`viewer-${url}-${i}`}
                src={url}
                alt={carouselImages.length > 1 ? `${displayItemName} — photo ${i + 1}` : displayItemName}
                className="h-full w-full shrink-0 snap-center object-contain"
              />
            ))}
          </div>

          {carouselImages.length > 1 && (
            <>
              <div className="pointer-events-none absolute bottom-[calc(env(safe-area-inset-bottom)+1.25rem)] left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5">
                {carouselImages.map((_, i) => (
                  <span
                    key={i}
                    className={`h-1.5 rounded-full transition-all ${
                      i === activeImageIndex ? "w-4 bg-white" : "w-1.5 bg-white/60"
                    }`}
                  />
                ))}
              </div>
              <div className="pointer-events-none absolute bottom-[calc(env(safe-area-inset-bottom)+1.25rem)] right-4 z-10 rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur">
                {Math.min(activeImageIndex + 1, carouselImages.length)} / {carouselImages.length}
              </div>
            </>
          )}
        </div>
      )}
      {item && Boolean(item.soldByWeight) && (
        <WeightSizePicker
          open={showWeightPicker}
          onOpenChange={setShowWeightPicker}
          itemId={item.id}
          itemName={item.name}
          tierLabel={(item as any).sizeTier}
          availableUnits={availableUnits}
          onSelected={addResolvedWeightUnit}
        />
      )}
      </>
      )}
    </div>
  );
}
